/**
 * The drop (02 §3; ROUND7 §5 station 1): kick a pod before the light turns green, jump on the green, steer the
 * canopy. The aircraft flies the whole course north to south first - the recon pass - so the first line says
 * what he is looking at, and the kick is asked for over the open ground just north of the drop band, where a
 * pod comes down on the lane he will walk.
 */

import { COURSE } from '../../gen/training';
import { INSERTION } from '../../sim/tuning';
import type { World } from '../../sim/types';
import type { Step } from './stepTypes';

/** Where he would come down on his present heading if he stopped steering now, carried by the course's wind. */
export function landingZ(w: World): number {
  const ins = w.insertion;
  const seconds = Math.max(0, ins.altitude) / INSERTION.sinkRate;
  return ins.z + (Math.cos(ins.heading) * INSERTION.glideSpeed + COURSE.wind.z) * seconds;
}

/** How far south a canopy left on its line carries him from the jump, glide and wind together, to 5 m. */
export const GLIDE_M =
  Math.round(((INSERTION.glideSpeed + COURSE.wind.z) * (INSERTION.altitude / INSERTION.sinkRate)) / 5) * 5;

/** Past this, a landing is in the badlands and the mission is scrubbed (insertion.ts). */
export const BADLANDS_Z = COURSE.dropBand.z1 + INSERTION.badlandsMargin;

const kick: Step = {
  id: 'kick',
  station: 'THE DROP',
  title: 'KICK OUT A POD',
  text: ({ world, keys }) => (world.insertion.z < COURSE.podClearing.z0
    // ROUND7 integration: it also promised a strip map on the right that fills in - the drop HUD has none.
    ? 'THE RECON PASS: YOU FLY THE WHOLE COURSE, NORTH TO SOUTH. WATCH THE GROUND GO BY UNDER YOU.'
    : `KICK OUT A SUPPLY POD NOW: PRESS ${keys.key('fire')} - BEFORE THE LIGHT TURNS GREEN.`),
  // ROUND8 §5: the course has a light wind now (for the dog lesson), and it carries a falling pod a few metres.
  note: ({ keys }) => `A POD COMES DOWN WHERE YOU LET IT GO, CARRIED A FEW METRES BY THE WIND. `
    + `${keys.key('moveLeft')} / ${keys.key('moveRight')} STEER THE AIRCRAFT.`,
  ready: ({ world }) => world.insertion.phase === 'flyover' && !world.insertion.greenLight,
  done: ({ facts }) => facts.kicked,
};

const jump: Step = {
  id: 'jump',
  station: 'THE DROP',
  title: 'JUMP',
  text: ({ world, keys }) => (world.insertion.greenLight
    ? `THE LIGHT IS GREEN: PRESS ${keys.key('fire')} TO JUMP - JUMP EARLY.`
    : `WAIT FOR THE GREEN LIGHT, THEN PRESS ${keys.key('fire')} TO JUMP.`),
  note: () => `UNDER THE CANOPY YOU GLIDE ABOUT ${GLIDE_M} M SOUTH. LAND PAST THE DROP BAND - IN THE BADLANDS - `
    + 'OR NEVER JUMP, AND THE MISSION IS SCRUBBED.',
  ready: ({ world }) => world.insertion.phase === 'flyover',
  done: ({ facts }) => facts.jumped,
};

const steer: Step = {
  id: 'steer',
  station: 'THE DROP',
  title: 'STEER THE CANOPY',
  text: ({ world, keys }) => (landingZ(world) > BADLANDS_Z - 2
    ? `TURN NOW - YOU ARE GLIDING INTO THE BADLANDS: HOLD ${keys.key('moveLeft')} OR ${keys.key('moveRight')}.`
    : `STEER THE CANOPY: ${keys.key('moveLeft')} / ${keys.key('moveRight')}. `
      + 'TURNING SPILLS AIR - YOU COME DOWN FASTER.'),
  note: () => 'THE SHADOW ON THE GROUND IS WHERE YOU ARE OVER; ITS SIZE IS YOUR HEIGHT. LAND ON OPEN GROUND.',
  ready: ({ world }) => world.insertion.phase === 'descent',
  done: ({ facts }) => facts.steered,
};

export const AIR_STEPS: readonly Step[] = [kick, jump, steer];
